import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { EventService } from '@core/services/event.service';
import { EmployeeInfoService } from '@core/services/employee-info.service';
import { NotificationsService } from '@core/services/notifications.service';
import { UtilityService } from '@core/services/utility.service';
import { ApiResponse } from '../../shared/model/api-response.model';
import { BellNotificationModel, EmployeeInfo } from '@typeDefs/home/responses/home';
import { BellNotficationApiService } from '@core/services/bell-notification.api.service';

@Injectable({
  providedIn: 'root',
})
export class BellNotficationService {
  private readonly bellNotficationApiService = inject(BellNotficationApiService);
  private readonly eventService = inject(EventService);
  private readonly employeeInfoService = inject(EmployeeInfoService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly toastr = inject(ToastrService);
  private readonly utilityService = inject(UtilityService);
  private readonly router = inject(Router);

  private employeeInfo: EmployeeInfo = this.employeeInfoService.getEmployeeInfo();
  private subscriptions: Subscription[] = [];

  public bellNotifications: BellNotificationModel[] = [];
  public hasNewNotification = false;

  constructor() {
    this.subscriptions.push(
      this.eventService.onSendDismissBellNotification((bellNotificationID: bigint) => this.removeBellNotification(bellNotificationID))
    );
    this.subscriptions.push(
      this.eventService.onSendUpdateBellNotification(
        (
          bellNotificationID: bigint,
          title: string,
          description: string,
          dateStarted: Date,
          dateCompleted: Date,
          status: number,
          actionType: number,
          actionURL: string,
          progress: number,
          errorMessage: string
        ) =>
          this.updateBellNotification(
            bellNotificationID,
            title,
            description,
            dateStarted,
            dateCompleted,
            status,
            actionType,
            actionURL,
            progress,
            errorMessage
          )
      )
    );
    this.subscriptions.push(
      this.eventService.onSendUpdateBellNotificationProgress((bellNotificationID: bigint, progress: number) =>
        this.updateBellNotificationProgress(bellNotificationID, progress)
      )
    );
    this.subscriptions.push(this.eventService.onSendRefreshBellNotifications(() => this.getBellNotifications()));

    this.getBellNotifications();
  }

  onDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.subscriptions = [];
  }

  getBellNotifications(): void {
    this.bellNotficationApiService.getBellNotifications(this.employeeInfo.UserKeyID).subscribe({
      next: (response: BellNotificationModel[]) => {
        this.bellNotifications = response;
      },
      error: () => {
        this.notificationsService.showError('Failed to get bell notifications', null);
      },
    });
  }

  public hasPendingBellNotification(): boolean {
    return this.bellNotifications.some(bellNotification => bellNotification.Status == 0);
  }

  executeActionURL(userKeyID: number, bellNotificationID: bigint, actionType: number, actionURL: string): void {
    if (actionURL == null) {
      return;
    }

    this.bellNotficationApiService.dismissBellNotification(userKeyID, bellNotificationID).subscribe({
      next: (response: ApiResponse) => {
        if (response.success) {
          this.removeBellNotification(bellNotificationID);

          if (actionType == 0) {
            // file action
            window.open(actionURL, '_self');
          } else if (actionType == 1) {
            // route URL action
            const currentUrl = this.router.url;
            const targetRouterUrl = this.getRouterUrlFromFullUrl(actionURL);

            if (currentUrl === targetRouterUrl) {
              // since current page is already the 'actionURL', calling the router navigate will do nothing.
              // so to force a "refresh" of the current view without a hard reload
              this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigateByUrl(currentUrl);
              });
            } else {
              this.router.navigateByUrl(targetRouterUrl);
            }
          }
        }
      },
    });
  }

  private getRouterUrlFromFullUrl(fullUrl: string): string {
    try {
      const url = new URL(fullUrl);
      return url.pathname + url.search + url.hash;
    } catch {
      // If it's already a relative URL, return as-is
      return fullUrl;
    }
  }

  public removeBellNotification(bellNotificationID: bigint): void {
    this.bellNotifications = this.bellNotifications.filter(bellNotification => bellNotification.BellNotificationID != bellNotificationID);
  }

  public updateBellNotification(
    bellNotificationID: bigint,
    title: string,
    description: string,
    dateStarted: Date,
    dateCompleted: Date,
    status: number,
    actionType: number,
    actionUrl: string,
    progress: number = null,
    errorMessage: string
  ): void {
    let updatedBellNotification!: BellNotificationModel;
    let prevStatus: number = null;

    // try to find bell notification
    const bellNotification = this.bellNotifications.find(bellNotification => bellNotification.BellNotificationID == bellNotificationID);

    // if bell notification found, update it, else add it
    if (bellNotification) {
      prevStatus = bellNotification.Status;
      bellNotification.Title = title;
      bellNotification.Description = description;
      bellNotification.DateStarted = dateStarted;
      bellNotification.DateCompleted = dateCompleted;
      bellNotification.Status = status;
      bellNotification.ActionType = actionType;
      bellNotification.ActionURL = actionUrl;
      bellNotification.ErrorMessage = errorMessage;

      updatedBellNotification = bellNotification;
    } else {
      this.bellNotifications.unshift({
        BellNotificationID: bellNotificationID,
        Title: title,
        Description: description,
        DateStarted: dateStarted,
        DateCompleted: dateCompleted,
        Status: status,
        ActionType: actionType,
        ActionURL: actionUrl,
        ErrorMessage: errorMessage,
      });

      updatedBellNotification = this.bellNotifications[0];
    }

    this.hasNewNotification = true;

    this.updateBellNotificationProgress(updatedBellNotification, progress);

    // show toast message, as long as we're not moving from enqueued status to processing
    if (prevStatus === null || !(prevStatus === 3 && updatedBellNotification.Status === 0)) {
      this.showToastMessage(title, status);
    }
  }

  // Overloaded method signatures
  updateBellNotificationProgress(bellNotification: BellNotificationModel, progress: number): void;
  updateBellNotificationProgress(bellNotificationID: bigint, progress: number): void;

  // Implementation
  updateBellNotificationProgress(param1: BellNotificationModel | bigint, progress: number): void {
    let bellNotification: BellNotificationModel | undefined;

    if (typeof param1 === 'bigint') {
      bellNotification = this.bellNotifications.find(bellNotification => bellNotification.BellNotificationID == param1);
    } else {
      bellNotification = param1;
    }

    if (bellNotification == null) {
      return;
    }

    // only assign progress if status is 'Processing', otherwise null progress value
    if (bellNotification.Status != 0) {
      progress = null;
    }

    if (bellNotification) {
      bellNotification.Progress = progress < 0 ? 0 : progress > 100 ? 100 : progress;
    }
  }

  private showToastMessage(title: string, status: number): void {
    const configOptions: Partial<IndividualConfig> = {
      timeOut: 3000,
      disableTimeOut: status == 2 ? true : false,
      progressBar: false,
      closeButton: true,
      tapToDismiss: true,
      positionClass: this.utilityService.isMobileMode() ? 'toast-top-left' : 'toast-top-right',
    };
    const messagePending = 'Item has been queued.';
    const messageComplete = 'Item is ready.';
    const messageError = 'Error while processing item.';
    const messageWarning = 'Item is ready, but warnings were generated.';

    let message: string;
    if (status == 1) {
      message = messageComplete;
    } else if (status == 2) {
      message = messageError;
    } else if (status == 4) {
      message = messageWarning;
    } else {
      message = messagePending;
    }

    if (status == 2) {
      this.toastr.error(message, title, configOptions);
    } else if (status == 4) {
      this.toastr.warning(message, title, configOptions);
    } else {
      this.toastr.success(message, title, configOptions);
    }
  }
}
